import {FolioItem, FolioItemType} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {formatDateHM, formatDayMonth, isSameDay} from "crqlar-react-common/dist/utils/date.util";

/*
    A folio line's amounts, computed exactly as the backend stores them
    (FolioItemServiceV3.cleanupBeforeSave) and adds them up (FolioServiceV3.calculateFolioTotal),
    so what the editor shows is what finalizing will charge. All amounts in cents.
*/

export interface LineAmounts {
    unitCents: number
    quantity: number
    /** How much the discounts take off unit × quantity. */
    discountCents: number
    grossCents: number
    netCents: number
    vatCents: number
}

/** Included in a package: shown, but not charged on its own. */
export const isIncludedLine = (line: FolioItem): boolean => line.inclusive === true;

export const isTipLine = (line: FolioItem): boolean => line.itemCategory === FolioItemType.TIP;

/** A VAT id, or a snapshot with a rate - an explicit 0 % counts. */
export const hasVat = (line: FolioItem): boolean => !!line.taxRateId || line.taxRateSnap?.rate != null;

export const lineQuantity = (line: FolioItem): number => line.quantity ?? 1;

export const lineAmounts = (line: FolioItem): LineAmounts => {
    const unitCents = line.price?.amountCents ?? 0;
    const quantity = lineQuantity(line);
    const beforeDiscounts = unitCents * quantity;

    // Each discount in order: the amount off first, then the percentage of what is left (truncated)
    let grossCents = beforeDiscounts;
    (line.discounts ?? []).forEach(discount => {
        grossCents -= discount.amountCentsOff ?? 0;
        const percentOff = discount.percentOff ?? 0;
        if (percentOff > 0) grossCents -= Math.trunc(grossCents * (percentOff / 100));
    });

    // Only an inclusive rate takes the VAT out of the price; exclusive and no VAT leave net = gross.
    // The backend's snapshot defaults are rate 0 and inclusive.
    const taxRate = line.taxRateSnap;
    const netCents = taxRate && (taxRate.inclusive ?? true)
        ? Math.trunc(grossCents / (1 + (taxRate.rate ?? 0) / 100))
        : grossCents;

    return {
        unitCents,
        quantity,
        discountCents: beforeDiscounts - grossCents,
        grossCents,
        netCents,
        vatCents: grossCents - netCents,
    };
};

export interface FolioTotals {
    /** Every charged line but tips, discounts taken off. */
    subtotalCents: number
    /** What the discounts take off those lines: the subtotal before them is subtotal + discounts. */
    discountCents: number
    vatCents: number
    tipCents: number
    /** What the backend's calculateTotal returns: every line that isn't archived or included. */
    totalCents: number
}

export const folioTotals = (lines: FolioItem[]): FolioTotals => lines
    .filter(line => !line.archived && !isIncludedLine(line))
    .reduce<FolioTotals>((totals, line) => {
        const {grossCents, discountCents, vatCents} = lineAmounts(line);
        const tip = isTipLine(line);

        return {
            subtotalCents: totals.subtotalCents + (tip ? 0 : grossCents),
            discountCents: totals.discountCents + (tip ? 0 : discountCents),
            vatCents: totals.vatCents + vatCents,
            tipCents: totals.tipCents + (tip ? grossCents : 0),
            totalCents: totals.totalCents + grossCents,
        };
    }, {subtotalCents: 0, discountCents: 0, vatCents: 0, tipCents: 0, totalCents: 0});

/** A price that isn't the service's rate on the line's date - or any price when there is no rate. */
export const isHandSetPrice = (line: FolioItem, ratePriceCents?: number): boolean => {
    const cents = line.price?.amountCents;
    return cents != null && cents !== ratePriceCents;
};

export type FolioLineIssue = 'NO_SERVICE' | 'NO_PRICE' | 'NO_VAT';

/** Issues Finalize can't pass; a missing VAT is a warning staff may accept. */
export const isBlockingIssue = (issue: FolioLineIssue): boolean => issue !== 'NO_VAT';

export interface LineIssueOptions {
    /** Every charged line needs a VAT rate (Casablanca books VAT exactly as transmitted). */
    vatRequired?: boolean
}

/** What a line lacks for Finalize. Included and tip lines are never charged on their own terms. */
export const lineIssues = (line: FolioItem, {vatRequired = false}: LineIssueOptions = {}): FolioLineIssue[] => {
    if (line.archived || isIncludedLine(line) || isTipLine(line)) return [];

    const issues: FolioLineIssue[] = [];
    if (!line.serviceId && !line.description?.trim()) issues.push('NO_SERVICE');
    if (line.price?.amountCents == null) issues.push('NO_PRICE');
    if (vatRequired && !hasVat(line)) issues.push('NO_VAT');
    return issues;
};

export interface FolioLineIssues {
    /** Position in the list that was checked. */
    index: number
    issues: FolioLineIssue[]
}

/** The lines with issues, in order. */
export const folioIssues = (lines: FolioItem[], options?: LineIssueOptions): FolioLineIssues[] => lines
    .map((line, index) => ({index, issues: lineIssues(line, options)}))
    .filter(entry => entry.issues.length > 0);

export const hasBlockingIssues = (issues: FolioLineIssues[]): boolean => (
    issues.some(entry => entry.issues.some(isBlockingIssue))
);

/** "29.09." when the line isn't on the reference day (the reservation's), otherwise nothing. */
export const lineDatePrefix = (start?: Date, referenceDay?: Date): string | undefined => {
    if (!start || (referenceDay && isSameDay(start, referenceDay))) return undefined;
    return `${formatDayMonth(start)}.`;
};

/** "14:00", or "29.09. 14:00" for a line on another day than the reference day. */
export const lineTimeLabel = (start?: Date, referenceDay?: Date): string => {
    if (!start) return '';
    return [lineDatePrefix(start, referenceDay), formatDateHM(start)].filter(Boolean).join(' ');
};
